import React, { useEffect, useState, useRef } from 'react';
import { Users, Send, Image as ImageIcon, Reply, X, Mic, Square, Trash2, RefreshCw } from 'lucide-react';

const Community = () => {
  const [activeTab, setActiveTab] = useState('general'); // 'general' or 'ai'
  const API_BASE = process.env.NODE_ENV === 'development' ? '' : 'http://localhost:3001';

  // General Chat state
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('groupChat')) || []; } catch { return []; }
  });

  // AI Chat state
  const [aiMessages, setAiMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('aiChat')) || []; } catch { return []; }
  });

  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [replyToId, setReplyToId] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('groupChat', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('aiChat', JSON.stringify(aiMessages));
  }, [aiMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, aiMessages, activeTab]);

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const startVoice = () => {
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) {
        alert('Voice input supported nahi hai is browser me.');
        return;
      }
      const rec = new SR();
      recognitionRef.current = rec;
      rec.lang = 'hi-IN';
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.continuous = false;
      rec.onstart = () => setIsRecording(true);
      rec.onresult = (event) => {
        const last = event.results[event.results.length - 1][0]?.transcript || '';
        const next = last.trim();
        if (next) setText(prev => (prev ? `${prev} ${next}` : next));
      };
      rec.onerror = () => setIsRecording(false);
      rec.onend = () => setIsRecording(false);
      rec.start();
    } catch {
      setIsRecording(false);
    }
  };

  const stopVoice = () => {
    try {
      recognitionRef.current && recognitionRef.current.stop();
    } catch { }
    setIsRecording(false);
  };

  const send = async () => {
    if (!text.trim() && !image) return;

    const msg = {
      id: Date.now(),
      text: text.trim(),
      image,
      ts: new Date().toISOString(),
      sender: 'You',
      replyToId: activeTab === 'general' ? replyToId : null,
    };

    let targetTab = activeTab;
    if (activeTab === 'general' && image) {
      targetTab = 'ai';
      setActiveTab('ai');
    }

    if (targetTab === 'general') {
      setMessages(prev => [...prev, msg]);
      setText('');
      setImage(null);
      setReplyToId(null);
    } else {
      // AI Chat Logic
      setAiMessages(prev => [...prev, msg]);
      setText('');
      // image ko tab tak clear na kare jab tak response aa jaye, UX ke liye rakhte hain
      setLoadingAi(true);

      try {
        let aiText = "I'm sorry, I couldn't understand that.";
        if (msg.image) {
          const base64 = msg.image.split(',')[1];
          const mime = msg.image.split(';')[0].split(':')[1];
          const response = await fetch(`${API_BASE}/api/gemini/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mime_type: mime,
              data: base64,
              prompt: msg.text,
            }),
          });


          if (response.status === 404) {
            throw new Error('API Endpoint not found (404). Check server config.');
          }

          const raw = await response.text();
          let data = null;
          try {
            data = raw ? JSON.parse(raw) : null;
          } catch {
            // Prevent showing HTML in error message
            console.error('Server returned non-JSON response:', raw.substring(0, 100)); // Log for dev
            throw new Error('Server returned an invalid response (not JSON).');
          }

          if (response.status === 429) {
            const wait = (data && data.retry_after_sec) ? Number(data.retry_after_sec) : 60;
            const infoMsg = {
              id: Date.now() + 2,
              text: `Quota exceed. ${wait}s baad retry ho raha hai...`,
              ts: new Date().toISOString(),
              sender: 'AI Crop Doctor',
            };
            setAiMessages(prev => [...prev, infoMsg]);
            await new Promise(res => setTimeout(res, Math.max(1, wait) * 1000));
            const retry = await fetch(`${API_BASE}/api/gemini/analyze`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                mime_type: mime,
                data: base64,
                prompt: msg.text,
              }),
            });
            const retryRaw = await retry.text();
            try {
              data = retryRaw ? JSON.parse(retryRaw) : null;
            } catch {
              throw new Error('Retry failed. Please try again later.');
            }
            if (!retry.ok || (data && data.error)) {
              const emsg = (data && data.error && data.error.message) ? data.error.message : (retryRaw?.trim() || 'request failed');
              throw new Error(emsg);
            }
          } else if (data && data.error) {
            aiText = `Error: ${data.error.message}`;
          } else if (data && data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts) {
            const part = data.candidates[0].content.parts.find(p => typeof p.text === 'string');
            aiText = part?.text || aiText;
            try {
              const cleaned = aiText.replace(/```json\n?|\n?```/g, '').trim();
              const obj = JSON.parse(cleaned);
              if (obj && typeof obj === 'object') {
                const lines = [];
                if (obj.plant_part || obj.leaf_color) {
                  const part = obj.plant_part ? `${obj.plant_part}` : '';
                  const color = obj.leaf_color ? `${obj.leaf_color}` : '';
                  lines.push(`Patte ka hissa: ${part || '-'}, Rang: ${color || '-'}`);
                }
                if (obj.leaf_edge_type) {
                  lines.push(`Patte ki kinari: ${obj.leaf_edge_type}`);
                }
                if (obj.damage_present) {
                  const types = Array.isArray(obj.damage_details?.type) ? obj.damage_details.type.join(', ') : obj.damage_details?.type || '-';
                  lines.push(`Nuksan: ${types}`);
                  if (obj.damage_details?.hole_description) {
                    lines.push(`Chhed ka varnan: ${obj.damage_details.hole_description}`);
                  }
                  if (obj.damage_details?.discoloration_description) {
                    lines.push(`Rang badlav: ${obj.damage_details.discoloration_description}`);
                  }
                }
                if (obj.overall_condition) {
                  lines.push(`Kul sthiti: ${obj.overall_condition}`);
                }
                if (obj.background_elements) {
                  lines.push(`Peechhe ke tatv: ${obj.background_elements}`);
                }
                aiText = lines.join('\n');
              }
            } catch { }
          }
        } else {
          const response = await fetch(`${API_BASE}/api/gemini/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt: msg.text }),
          });

          if (!response.ok) {
            if (response.status === 404) throw new Error('API Endpoint not found (404).');
            throw new Error(`Server status: ${response.status}`);
          }

          const raw = await response.text();
          let data = null;
          try {
            data = raw ? JSON.parse(raw) : null;
          } catch {
            console.error('Chat API returned non-JSON:', raw.substring(0, 100));
            throw new Error('Server returned invalid response.');
          }

          if (data && data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts) {
            aiText = data.candidates[0].content.parts[0].text;
          } else if (data && data.error) {
            aiText = `Error: ${data.error.message}`;
          }
        }

        const aiMsg = {
          id: Date.now() + 1,
          text: aiText,
          ts: new Date().toISOString(),
          sender: 'AI Crop Doctor',
        };
        setAiMessages(prev => [...prev, aiMsg]);
        setImage(null);
      } catch (e) {
        console.error("AI Chat Error:", e);
        let errorText = "Network error. Please ensure the backend server is running.";
        if (e.message.includes('Server status: 404')) {
          errorText = "API endpoint not found. Is the server updated?";
        } else if (e.message.includes('Server status: 500')) {
          errorText = "Server error. Check API key.";
        }

        const errMsg = {
          id: Date.now() + 1,
          text: errorText,
          ts: new Date().toISOString(),
          sender: 'AI Crop Doctor',
        };
        setAiMessages(prev => [...prev, errMsg]);
      } finally {
        setLoadingAi(false);
      }
    }
  };

  const quoted = (id) => messages.find(m => m.id === id);

  const clearChat = () => {
    const isGeneral = activeTab === 'general';
    const confirmMsg = isGeneral
      ? 'Are you sure you want to delete this conversation for everyone? (Local only in demo)'
      : 'Start a new chat with AI Crop Doctor?';

    if (window.confirm(confirmMsg)) {
      if (isGeneral) {
        setMessages([]);
        localStorage.removeItem('groupChat');
      } else {
        setAiMessages([]);
        localStorage.removeItem('aiChat');
      }
    }
  };

  const currentMessages = activeTab === 'general' ? messages : aiMessages;

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col md:flex-row bg-slate-50 relative overflow-hidden">

      {/* Mobile Tabs (Visible only on small screens) */}
      <div className="md:hidden bg-white border-b border-slate-200 flex items-center justify-center p-2 space-x-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 rounded-lg font-bold text-sm transition-all flex items-center space-x-2 ${activeTab === 'general' ? 'bg-emerald-100 text-emerald-700' : 'text-slate-500 hover:bg-slate-50'}`}
        >
          <Users className="w-4 h-4" />
          <span>Community</span>
        </button>
        <button
          onClick={() => setActiveTab('ai')}
          className={`px-4 py-2 rounded-lg font-bold text-sm transition-all flex items-center space-x-2 ${activeTab === 'ai' ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-50'}`}
        >
          <div className="w-4 h-4 bg-gradient-to-tr from-blue-500 to-purple-500 rounded-full animate-pulse"></div>
          <span>AI Crop Doctor</span>
        </button>
      </div>

      {/* Desktop Sidebar (Visible only on medium/large screens) */}
      <div className="hidden md:flex flex-col w-80 bg-white border-r border-slate-200 h-full flex-shrink-0">
        <div className="p-5 border-b border-slate-100">
          <h2 className="text-xl font-black text-slate-800 tracking-tight">Discussions</h2>
          <p className="text-slate-500 text-sm mt-1">Join the community</p>
        </div>

        <div className="p-3 space-y-2 overflow-y-auto flex-1">
          <button
            onClick={() => setActiveTab('general')}
            className={`w-full text-left px-4 py-4 rounded-xl transition-all flex items-center space-x-4 group ${activeTab === 'general' ? 'bg-emerald-50 ring-1 ring-emerald-200 shadow-sm' : 'hover:bg-slate-50'}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm transition-colors ${activeTab === 'general' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-white group-hover:text-emerald-500'}`}>
              <Users className="w-5 h-5" />
            </div>
            <div>
              <div className={`font-bold ${activeTab === 'general' ? 'text-emerald-900' : 'text-slate-700'}`}>General Chat</div>
              <div className="text-xs text-slate-500 mt-0.5">Agriculture Community</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('ai')}
            className={`w-full text-left px-4 py-4 rounded-xl transition-all flex items-center space-x-4 group ${activeTab === 'ai' ? 'bg-blue-50 ring-1 ring-blue-200 shadow-sm' : 'hover:bg-slate-50'}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm transition-colors ${activeTab === 'ai' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-white group-hover:text-blue-600'}`}>
              <div className={`w-5 h-5 bg-current rounded-full ${activeTab !== 'ai' ? 'opacity-50' : ''}`} style={{ background: activeTab === 'ai' ? 'white' : 'currentColor', maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z\'/%3E%3C/svg%3E")', maskRepeat: 'no-repeat', maskPosition: 'center' }}></div>
              {/* Fallback icon if mask tricky, or just use text/emoji */}
              <div className="font-bold text-xs">AI</div>
            </div>
            <div>
              <div className={`font-bold ${activeTab === 'ai' ? 'text-blue-900' : 'text-slate-700'}`}>AI Crop Doctor</div>
              <div className="text-xs text-slate-500 mt-0.5">Instant Expert Help</div>
            </div>
          </button>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center space-x-3 text-slate-500 text-xs text-center justify-center">
            <span>&copy; AgriScan Community</span>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full bg-[#f0f2f5] relative min-w-0"> {/* min-w-0 fixes flex overflow issues */}
        {/* Detail Background Pattern */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(#4b5563 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

        {/* Chat Header for Context (Mobile mostly, or Desktop context) */}
        <div className="bg-white/90 backdrop-blur-md px-4 py-3 border-b border-slate-200 shadow-sm flex items-center justify-between z-10 sticky top-0">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${activeTab === 'general' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`}>
              {activeTab === 'general' ? <Users className="w-5 h-5" /> : <div className="font-black text-lg">AI</div>}
            </div>
            <div>
              <div className="font-bold text-slate-800 text-lg leading-tight">
                {activeTab === 'general' ? 'General Chat' : 'AI Assistant'}
              </div>
              <div className={`text-xs font-medium flex items-center ${activeTab === 'general' ? 'text-emerald-600' : 'text-blue-600'}`}>
                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 animate-pulse ${activeTab === 'general' ? 'bg-emerald-500' : 'bg-blue-500'}`}></span>
                {activeTab === 'general' ? 'Online' : 'Active'}
              </div>
            </div>
          </div>

          <button
            onClick={clearChat}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-full transition-all shadow-sm border active:scale-95 ${activeTab === 'general'
              ? 'bg-red-50 text-red-600 border-red-100 hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 hover:text-white hover:border-red-500 hover:shadow-red-200'
              : 'bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-indigo-600 hover:text-white hover:border-indigo-500 hover:shadow-indigo-200'
              }`}
            title={activeTab === 'general' ? 'Delete Conversation' : 'Reset Chat'}
          >
            {activeTab === 'general' ? <Trash2 className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
            <span className="hidden md:inline text-sm font-semibold">
              {activeTab === 'general' ? 'Clear Chat' : 'Reset'}
            </span>
          </button>
        </div>

        {/* Messages List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 z-0 max-w-5xl mx-auto w-full">
          {currentMessages.map(m => (
            <div key={m.id} className={`flex w-full ${m.sender === 'You' ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex max-w-[85%] md:max-w-[70%] ${m.sender === 'You' ? 'flex-row-reverse' : 'flex-row'}`}>
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold shadow-sm ${m.sender === 'You'
                  ? 'bg-emerald-100 text-emerald-700 ml-2'
                  : (m.sender === 'AI Crop Doctor' ? 'bg-blue-600 text-white mr-2' : 'bg-white text-slate-600 mr-2')
                  }`}>
                  {m.sender === 'You' ? 'ME' : (m.sender === 'AI Crop Doctor' ? 'AI' : 'U')}
                </div>

                {/* Message Bubble */}
                <div className="flex flex-col">
                  <div
                    className={`relative px-4 py-2 shadow-sm cursor-pointer group transition-all 
                              ${m.sender === 'You'
                        ? 'bg-emerald-600 text-white rounded-2xl rounded-tr-sm'
                        : (m.sender === 'AI Crop Doctor' ? 'bg-white text-slate-800 border-l-4 border-blue-500 rounded-2xl rounded-tl-sm' : 'bg-white text-slate-800 rounded-2xl rounded-tl-sm')
                      }`}
                    onClick={() => activeTab === 'general' && setReplyToId(m.id)}
                  >
                    {/* Reply Context (General Only) */}
                    {activeTab === 'general' && m.replyToId && (
                      <div className={`mb-2 rounded-lg px-3 py-1.5 text-xs border-l-2 ${m.sender === 'You' ? 'bg-emerald-700/50 border-emerald-300 text-emerald-50' : 'bg-slate-50 border-emerald-500 text-slate-600'}`}>
                        <div className="font-bold opacity-80 mb-0.5">Reply to</div>
                        <div className="truncate">{quoted(m.replyToId)?.text || 'Photo attachment'}</div>
                      </div>
                    )}

                    {/* Image Attachment */}
                    {m.image && (
                      <div className="mb-2 mt-1">
                        <img src={m.image} alt="attachment" className="rounded-xl max-w-full max-h-60 object-cover border border-black/10" />
                      </div>
                    )}

                    {/* Text Content */}
                    {m.text && <div className="leading-relaxed whitespace-pre-wrap text-sm md:text-base">{m.text}</div>}

                    {/* Timestamp */}
                    <div className={`text-[10px] mt-1 text-right opacity-70 ${m.sender === 'You' ? 'text-emerald-100' : 'text-slate-400'}`}>
                      {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>

                    {/* Hover Reply Icon (General Only) */}
                    {activeTab === 'general' && (
                      <div className={`absolute top-2 opacity-0 group-hover:opacity-100 transition-opacity ${m.sender === 'You' ? '-left-8' : '-right-8'}`}>
                        <button className="p-1 bg-slate-200 hover:bg-slate-300 rounded-full text-slate-600"><Reply className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {loadingAi && (
            <div className="flex w-full justify-start">
              <div className="flex max-w-[85%] flex-row">
                <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold shadow-sm bg-blue-600 text-white mr-2">AI</div>
                <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm flex items-center space-x-1">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          {currentMessages.length === 0 && !loadingAi && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 my-10">
              <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mb-4">
                {activeTab === 'general' ? <Users className="w-8 h-8 text-slate-400" /> : <div className="text-2xl font-bold text-slate-400">AI</div>}
              </div>
              <p>{activeTab === 'general' ? 'No messages yet. Start the conversation!' : 'Ask me anything about your crops!'}</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="bg-white px-4 py-3 border-t border-slate-200 z-10">
          <div className="max-w-5xl mx-auto w-full">
            {activeTab === 'general' && replyToId && (
              <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg mb-2 border-l-4 border-emerald-500">
                <div className="text-sm">
                  <span className="font-bold text-emerald-600 block">Replying to message</span>
                  <span className="text-slate-500 truncate block max-w-xs">{quoted(replyToId)?.text || 'Photo attachment'}</span>
                </div>
                <button onClick={() => setReplyToId(null)} className="p-1 hover:bg-slate-200 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
              </div>
            )}

            {image && (
              <div className="relative inline-block mb-3">
                <img src={image} alt="preview" className="h-24 rounded-xl border border-slate-200 shadow-sm" />
                <button onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition"><X className="w-3 h-3" /></button>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`p-2.5 rounded-full transition-colors ${activeTab === 'general' ? 'text-slate-500 hover:text-emerald-600 hover:bg-emerald-50' : 'text-slate-500 hover:text-blue-600 hover:bg-blue-50'}`}
              >
                <ImageIcon className="w-6 h-6" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />

              <div className="flex-1 bg-slate-100 rounded-full px-4 py-2 focus-within:ring-2 focus-within:ring-emerald-500/50 transition-shadow">
                <input
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder={activeTab === 'general' ? "Type a message..." : "Ask AI Crop Doctor..."}
                  className="w-full bg-transparent border-none focus:ring-0 text-slate-800 placeholder-slate-400"
                  onKeyDown={e => e.key === 'Enter' && send()}
                />
              </div>

              <button
                onClick={() => (isRecording ? stopVoice() : startVoice())}
                className={`p-3 rounded-full transition-all active:scale-95 shadow-lg ${activeTab === 'general' ? 'text-emerald-700 hover:bg-emerald-50' : 'text-blue-700 hover:bg-blue-50'}`}
                title={isRecording ? 'Stop Voice' : 'Start Voice'}
              >
                {isRecording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>

              <button
                onClick={send}
                disabled={!text.trim() && !image}
                className={`p-3 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all active:scale-95 flex items-center justify-center ${activeTab === 'general' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'}`}
              >
                <Send className="w-5 h-5 ml-0.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;
